import React, { useState, useRef, useCallback } from 'react';
import * as RovingFocus from '@radix-ui/react-roving-focus';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { InspectionListProps, InspectionItem } from './InspectionList.types';
import InspectionListItem from './InspectionListItem';

const InspectionList: React.FC<InspectionListProps> = ({
  items,
  className = '',
  ariaLabel = 'Lista elementów ',
  onSelectionChange,
}) => {
  const [selectedItem, setSelectedItem] = useState<InspectionItem | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const handleItemClick = useCallback(
    (item: InspectionItem) => {
      setSelectedItem(item);
      onSelectionChange?.(item);
    },
    [onSelectionChange],
  );

  const handleItemFocus = useCallback(
    (item: InspectionItem) => {
      const index = items.findIndex((i) => i.id === item.id);
      setFocusedIndex(index);
    },
    [items],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, item: InspectionItem) => {
      const currentIndex = items.findIndex((i) => i.id === item.id);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (currentIndex < items.length - 1) {
            setFocusedIndex(currentIndex + 1);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (currentIndex > 0) {
            setFocusedIndex(currentIndex - 1);
          }
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(items.length - 1);
          break;
        case 'Escape':
          setSelectedItem(null);
          onSelectionChange?.(null);
          break;
      }
    },
    [items, onSelectionChange],
  );

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      // Allow normal tab behavior
      return;
    }
  };

  return (
    <div className={className} ref={listRef}>
      <VisuallyHidden.Root>
        <h2 id="inspection-list-title">{ariaLabel}</h2>
      </VisuallyHidden.Root>

      <RovingFocus.Root asChild orientation="vertical" loop={true}>
        <div
          role="list"
          aria-labelledby="inspection-list-title"
          aria-describedby="inspection-list-description"
          aria-live="polite"
          aria-relevant="additions removals"
          onKeyDown={handleListKeyDown}
          className="flex flex-col gap-3 focus:outline-none"
        >
          {items.map((item, index) => (
            <InspectionListItem
              item={item}
              isSelected={selectedItem?.id === item.id}
              isFocused={focusedIndex === index}
              onClick={handleItemClick}
              onFocus={handleItemFocus}
              onKeyDown={handleKeyDown}
            />
          ))}
        </div>
      </RovingFocus.Root>

      {selectedItem && (
        <VisuallyHidden.Root>
          <div role="status" aria-live="assertive">
            Wybrano element: {selectedItem.name}
          </div>
        </VisuallyHidden.Root>
      )}

      {items.length === 0 && (
        <div
          role="status"
          aria-live="polite"
          className="py-8 text-center text-gray-500"
        >
          Brak elementów do wyświetlenia
        </div>
      )}
    </div>
  );
};

export default InspectionList;
